import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { CheckMark } from "../checkbox-indicator/checkbox-indicator";
import { Icon, type IconName } from "../icon";

const menuSurfaceVariants = cva(
  [
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-menu-default-outline",
    "bg-menu-default-background",
    "shadow-elevation-3",
    "p-(--spacing-4)",
    "flex",
    "flex-col",
    "max-h-[min(20rem,calc(100vh-2rem))]",
    "overflow-y-auto",
    "overscroll-contain",
    "outline-none",
    "data-[starting-style]:opacity-0",
    "data-[starting-style]:scale-95",
    "data-[ending-style]:opacity-0",
    "data-[ending-style]:scale-95",
    "transition-[opacity,transform]",
    "duration-100",
    "ease-in-out",
  ],
  {
    variants: {
      size: {
        sm: "w-[180px]",
        md: "w-[296px]",
        lg: "w-[400px]",
        anchor: "w-[var(--anchor-width)]",
        trigger:
          "w-[max(180px,var(--anchor-width))] max-w-[calc(100vw-var(--spacing-16))]",
        auto: "w-auto min-w-[180px] max-w-[calc(100vw-var(--spacing-16))]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const menuItemVariants = cva(
  [
    "flex",
    "h-[36px]",
    "w-full",
    "items-center",
    "gap-(--spacing-8)",
    "rounded-(--radius-sm)",
    "px-(--spacing-12)",
    "py-(--spacing-8)",
    "font-(family-name:--font-family-body)",
    "text-(length:--font-size-body-medium)",
    "font-(number:--font-weight-regular)",
    "leading-(--font-line-height-20)",
    "tracking-(--font-letter-spacing-default)",
    "text-menu-text-default",
    "cursor-pointer",
    "select-none",
    "outline-none",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "hover:bg-menu-item-hover",
    "active:bg-menu-item-pressed",
    "data-[highlighted]:bg-menu-item-hover",
    "data-[selected]:bg-menu-item-selected",
    "data-[checked]:bg-menu-item-selected",
    "[&[data-highlighted]:active]:bg-menu-item-pressed",
    "[&[data-selected]:active]:bg-menu-item-pressed",
    "[&[data-checked]:active]:bg-menu-item-pressed",
    "focus-visible:ring-2",
    "focus-visible:ring-inset",
    "focus-visible:ring-menu-focus-ring",
    "data-[disabled]:pointer-events-none",
    "data-[disabled]:cursor-default",
    "data-[disabled]:opacity-50",
    "data-[disabled]:hover:bg-transparent",
  ],
  {
    variants: {
      tone: {
        default: "",
        destructive: [
          "text-menu-text-destructive",
          "[&_svg]:text-menu-icon-destructive",
        ],
      },
    },
    defaultVariants: { tone: "default" },
  },
);

const menuSubHeaderClassName = cn(
  "flex",
  "h-(--spacing-24)",
  "w-full",
  "items-center",
  "rounded-(--radius-sm)",
  "px-(--spacing-12)",
  "py-(--spacing-4)",
  "font-(family-name:--font-family-label)",
  "text-(length:--font-size-label-small)",
  "font-(number:--font-weight-medium)",
  "leading-(--font-line-height-16)",
  "tracking-(--font-letter-spacing-default)",
  "text-menu-text-subtle",
  "select-none",
  "data-[disabled]:text-menu-text-disabled-sub",
);

export interface MenuSurfaceProps
  extends Omit<React.ComponentProps<"div">, "className">,
    VariantProps<typeof menuSurfaceVariants> {
  className?: string;
}

function menuSurfaceClassName({ size, className }: MenuSurfaceProps) {
  return cn(menuSurfaceVariants({ size }), className);
}

export interface MenuItemLayoutProps {
  children: React.ReactNode;
  leadingIcon?: IconName;
  endSection?: React.ReactNode;
  checked?: boolean;
  checkbox?: boolean;
  submenu?: boolean;
}

function MenuItemLayout({
  children,
  leadingIcon,
  endSection,
  checked,
  checkbox,
  submenu,
}: MenuItemLayoutProps) {
  return (
    <>
      {checkbox && <MenuCheckboxVisual checked={checked} />}
      {leadingIcon && (
        <span className="inline-flex shrink-0 text-menu-icon-default">
          <Icon name={leadingIcon} size="xs" tone="inherit" />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {checked && !checkbox && (
        <span className="inline-flex shrink-0 text-menu-icon-secondary">
          <Icon name="check-mark" size="xs" tone="inherit" />
        </span>
      )}
      {submenu ? (
        <span
          aria-hidden="true"
          className="inline-flex shrink-0 text-menu-icon-default"
        >
          <Icon name="chevron-right" size="xs" tone="inherit" />
        </span>
      ) : (
        endSection && (
          <span className="inline-flex shrink-0 items-center gap-(--spacing-4)">
            {endSection}
          </span>
        )
      )}
    </>
  );
}

interface MenuCheckboxVisualProps {
  checked?: boolean;
}

function MenuCheckboxVisual({ checked }: MenuCheckboxVisualProps) {
  return (
    <span
      aria-hidden="true"
      data-checked={checked ? "" : undefined}
      className="inline-flex h-(--icon-sm) w-(--icon-sm) shrink-0 items-center justify-center"
    >
      <span
        data-checked={checked ? "" : undefined}
        className={cn(
          "inline-flex",
          "h-(--icon-xs)",
          "w-(--icon-xs)",
          "items-center",
          "justify-center",
          "rounded-(--radius-sm)",
          "border-[1.5px]",
          "border-checkbox-outline-default-border",
          "bg-surface-container-lowest",
          "text-transparent",
          "transition-colors",
          "duration-200",
          "ease-in-out",
          "data-[checked]:border-transparent",
          "data-[checked]:bg-checkbox-filled-default-background",
          "data-[checked]:text-surface-container-lowest",
          "[[data-disabled]_&]:border-transparent",
          "[[data-disabled]_&]:bg-checkbox-filled-disabled-background",
          "[[data-disabled]_&]:text-surface-container-lowest",
        )}
      >
        <span className="h-[calc(var(--icon-xs)*0.8)] w-[calc(var(--icon-xs)*0.8)]">
          <CheckMark />
        </span>
      </span>
    </span>
  );
}

function menuItemClassName({
  tone,
  className,
}: VariantProps<typeof menuItemVariants> & { className?: string }) {
  return cn(menuItemVariants({ tone }), className);
}

export {
  MenuItemLayout,
  menuItemClassName,
  menuSubHeaderClassName,
  menuSurfaceClassName,
};
